import { useState, useEffect } from 'react';
import { useFormContext, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import { reduxPropsDataDefault } from './Data';
import TextField from 'components/TextField/TextField';
import { useAppSelector } from 'hooks/useRedux';
import SelectField from 'components/selectField/SelectField';
import TextareaField from 'components/TextareaField/TextareaField';
import RadioButton from 'components/RadioButton/RadioButton';
import SignatureField from 'components/SignatureField/SignatureField';
import DateField from 'components/DateField/DateField';
import Checkbox from 'components/Checkbox/Checkbox';
import { ValuesType } from 'state/forms/forms.reducer';
import { workflowSelectors } from 'state/workflow';
import InteractiveItemList from 'components/InteractiveItemList/InteractiveItemList';

export type formTypes =
  | 'text'
  | 'select'
  | 'textarea'
  | 'radio'
  | 'signature'
  | 'date'
  | 'checkbox'
  | 'interMenu';
export interface FormFieldProps {
  identifier: string;
  type: formTypes;
  className?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  units?: string;
  variant?: 'number' | 'text' | 'phone' | 'password' | 'numberAsText';
  radioVariant?: 'horizontal' | 'vertical';
  setValue?: (value: string) => void;
  setFormValue?: UseFormSetValue<ValuesType>;
  wholeNumber?: boolean;
  validation?: object;
  index?: number;
  step?: string | number;
  hideReq?: boolean;
  maxLength?: number;
  getValues?: UseFormGetValues<ValuesType>;
  setArrValue?: UseFormSetValue<ValuesType>;
  identifierForOther?: string;
  isIndicationInputEnabled?: boolean;
  registerForm?: boolean;
  isDisabled?: boolean;
  required?: boolean;
  typeahead?: string[];
  typeaheadErrorMessage?: string;
  shrinkErrorMessage?: boolean;
  labelOverride?: string;
  placeholder?: string;
  showDefaultLabel?: boolean;
}

const FormField = ({
  identifier,
  identifierForOther,
  style,
  className,
  type,
  readOnly,
  units,
  variant,
  step,
  setValue,
  radioVariant,
  maxLength,
  wholeNumber,
  validation,
  index,
  hideReq,
  getValues,
  setArrValue,
  isIndicationInputEnabled,
  registerForm,
  required,
  isDisabled = false,
  typeahead,
  typeaheadErrorMessage,
  shrinkErrorMessage,
  labelOverride,
  placeholder,
  showDefaultLabel,
}: FormFieldProps) => {
  const workflowForm = useAppSelector(workflowSelectors.workflowForm);
  const [field, setField] = useState(reduxPropsDataDefault);

  const {
    watch,
    control,
    formState: { errors },
    register,
    trigger,
    setValue: setFormValue,
  } = useFormContext();

  useEffect(() => {
    if (workflowForm) {
      const workflowField: {
        title: { eng: string };
        identifier: string;
        value: string;
        required: boolean;
        choices: { eng: string[] };
      } = workflowForm.getFieldByKey(identifier);
      if (workflowField) {
        setField(workflowField);
      }

      if (workflowField) {
        setField(workflowField);
      }
    }
  }, [workflowForm]);

  const setIdentifier = index ? `${index}:${identifier}` : identifier;
  const ComponentProps = {
    className: 'form-control',
    field: field,
    setField: setField,
  };

  const Components = {
    text: (
      <TextField
        {...ComponentProps}
        validation={validation}
        step={step}
        setValue={setValue}
        setFormValue={setFormValue}
        errors={errors}
        formKey={setIdentifier}
        wholeNumber={wholeNumber}
        register={register}
        readOnly={readOnly}
        units={units}
        hideReq={!field.required && hideReq}
        trigger={trigger}
        variant={variant || 'text'}
        title={labelOverride || (typeof field.title === 'object' ? field.title.eng : field.title)}
        required={!isDisabled && (field.required || required)}
        isDisabled={isDisabled}
        typeahead={typeahead}
        typeaheadErrorMessage={typeaheadErrorMessage}
        shrinkErrorMessage={shrinkErrorMessage}
        placeholder={placeholder}
      />
    ),

    select: (
      <SelectField
        {...ComponentProps}
        className={className}
        setValue={setValue}
        watch={watch}
        trigger={trigger}
        choices={field.choices}
        formKey={setIdentifier}
        errors={errors}
        register={register}
        label={typeof field.title === 'object' ? field.title.eng : field.title}
        isRequired={field.required}
        hideReq={hideReq}
        isDisabled={isDisabled}
      />
    ),

    textarea: (
      <TextareaField
        style={style}
        {...ComponentProps}
        formKey={setIdentifier}
        register={register}
        title={typeof field.title === 'object' ? field.title.eng : field.title}
        isDisabled={isDisabled}
        validation={validation}
        errors={errors}
        setValue={(value) => {
          setFormValue(setIdentifier, value);
        }}
        maxLength={maxLength}
      />
    ),

    radio: (
      <RadioButton
        {...ComponentProps}
        formKey={setIdentifier}
        errors={errors}
        register={register}
        required={field.required}
        identifierForOther={identifierForOther}
        isIndicationInputEnabled={isIndicationInputEnabled}
        setValue={setFormValue}
        registerForm={registerForm}
        isDisabled={isDisabled}
        orientation={radioVariant || 'vertical'}
        form={{
          id: 1,
          title: () => {
            if (radioVariant === 'horizontal') {
              return (
                <div>
                  <label>
                    {typeof field.title === 'object' ? field.title.eng : field.title}
                    {field.required && <span className="text-danger"> * </span>}
                    {showDefaultLabel && (
                      <div className="d-block d-lg-inline pb-0 pt-3 text-muted"> Select One:</div>
                    )}
                  </label>
                </div>
              );
            } else {
              return (
                <div>
                  <label>
                    {field.required && <span className="text-danger"> *</span>}
                    {typeof field.title === 'object' ? field.title.eng : field.title}
                    {showDefaultLabel && (
                      <div className="d-block d-lg-inline pb-0 pt-3 text-muted"> Select One:</div>
                    )}
                  </label>
                </div>
              );
            }
          },
          options: field.choices ? field.choices.eng : [],
        }}
      />
    ),

    interMenu: (
      <InteractiveItemList
        label={typeof field.title === 'object' ? field.title.eng : field.title}
        formKey={setIdentifier}
        setValue={setArrValue}
        control={control}
        errors={errors}
        required={field.required}
        register={register}
        getValues={getValues}
        isDisabled={isDisabled}
      />
    ),

    signature: (
      <SignatureField
        label={labelOverride || (typeof field.title === 'object' ? field.title.eng : field.title)}
        register={register}
        formKey={setIdentifier}
        setValue={setFormValue}
        control={control}
        errors={errors}
        required={field.required}
      />
    ),

    date: (
      <DateField
        {...ComponentProps}
        validation={validation}
        required={field.required}
        title={labelOverride || (typeof field.title === 'object' ? field.title.eng : field.title)}
        errors={errors}
        formKey={setIdentifier}
        register={register}
        isDisabled={isDisabled}
      />
    ),

    checkbox: (
      <Checkbox
        required={field.required}
        formKey={setIdentifier}
        register={register}
        setValue={setFormValue}
        title={typeof field.title === 'object' ? field.title.eng : field.title}
        isDisabled={isDisabled}
      />
    ),
  };

  const isTypeValid = (type: string) => {
    return Object.keys(Components).find((name) => name === type);
  };
  return <>{isTypeValid(type) && Components[type]}</>;
};

export default FormField;
